"use client";

import { Children, useCallback, useEffect, useRef, useState, type ReactNode } from "react";

const STEP = 20;

/**
 * Renders a long `<ul>` in slices, growing as the reader reaches the end.
 *
 * The whole list already lives in memory — search, filters and counts all need
 * it — so this bounds how much of it is mounted at once, not how much is
 * fetched. The sentinel is a real button so the list is still reachable by
 * keyboard and when the observer never fires.
 */
export function RevealOnScroll({
  children,
  step = STEP,
  resetKey,
}: {
  children: ReactNode;
  step?: number;
  /** Changing this starts the list from the top again, as a new filter should. */
  resetKey?: unknown;
}) {
  const items = Children.toArray(children);
  const [count, setCount] = useState(step);
  const [seenKey, setSeenKey] = useState(resetKey);
  const sentinelRef = useRef<HTMLButtonElement | null>(null);

  if (seenKey !== resetKey) {
    setSeenKey(resetKey);
    setCount(step);
  }

  const hasMore = count < items.length;
  const showMore = useCallback(() => setCount((current) => current + step), [step]);

  // Re-observing after each slice is what chains the loading: a fresh observer
  // reports the sentinel's current position, so a still-visible one loads again.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) showMore();
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [count, hasMore, showMore]);

  return (
    <>
      {items.slice(0, count)}
      {hasMore ? (
        <li>
          <button
            ref={sentinelRef}
            type="button"
            onClick={showMore}
            className="w-full px-4 py-3 text-center text-xs font-medium text-ink-subtle transition hover:text-ink"
          >
            Show more
          </button>
        </li>
      ) : null}
    </>
  );
}
